import React, { useMemo } from 'react';
import { useAppData } from 'src/components/AppDataContext';
import Header from 'src/components/common/Header';
import { getDetailRoute } from 'src/constants/routes';
import 'src/styles/MainPage.css';

const MainPage: React.FC = () => {
  const { materials, formulations, workOrders } = useAppData();

  // 공통 정렬 로직
  const sortByRecentDate = <T extends { createdAt: string; updatedAt?: string }>(data: T[]) =>
    [...data]
      .sort((a, b) => {
        const dateA = new Date(a.updatedAt || a.createdAt).getTime();
        const dateB = new Date(b.updatedAt || b.createdAt).getTime();
        return dateB - dateA;
      })
      .slice(0, 10);

  const recentMaterials = useMemo(() => sortByRecentDate(materials), [materials]);
  const recentFormulations = useMemo(() => sortByRecentDate(formulations), [formulations]);
  const recentWorkOrders = useMemo(() => sortByRecentDate(workOrders), [workOrders]);

  const getTitle = (dataType: 'MATERIALS' | 'FORMULATIONS' | 'WORK_ORDERS', item: any): string => {
    switch (dataType) {
      case 'MATERIALS':
        return item.modelName || 'No Model Name';
      case 'FORMULATIONS':
        return item.name || 'No Name';
      case 'WORK_ORDERS':
        return item.name || 'No Name';
      default:
        return 'Untitled';
    }
  };

  const renderSection = (
    title: string,
    dataType: 'MATERIALS' | 'FORMULATIONS' | 'WORK_ORDERS',
    items: any[]
  ) => (
    <section>
      <h2>{title}</h2>
      <div className="main-data-grid horizontal-scroll">
        {items.map(item => {
          const recentDate = new Date(item.updatedAt || item.createdAt);
          const isUpdated = !!item.updatedAt;
          return (
            <div className="main-data-card" key={item.id}>
              <div className="tag-container">
                <span className={`main-status-tag ${isUpdated ? 'updated' : 'new'}`}>
                  {isUpdated ? 'Updated' : 'New'}
                </span>
              </div>
              <a href={getDetailRoute(dataType, item.id)} className="main-data-title">
                {getTitle(dataType, item)}
              </a>
              <p className="main-data-metadata">
                by {item.updatedBy || item.createdBy} on {recentDate.toLocaleDateString()}
              </p>
            </div>
          );
        })}
      </div>
    </section>
  );

  return (
    <div>
      <Header />
      <div className="main-page">
        <aside className="sidebar">
          <section className="resources">
            <h2>📂 사용자 가이드</h2>
            <ul className="resource-list">
              <li>
                <a
                  href="https://drive.google.com/file/d/1kJiLL7KhlPLrWOANDCUT992RNMnxxV5l/view"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  PDF 사용법 보기
                </a>
              </li>
            </ul>
          </section>
        </aside>
        <main className="main-content">
          {renderSection('🧪 물질', 'MATERIALS', recentMaterials)}
          {renderSection('⚗️ 전극 조성', 'FORMULATIONS', recentFormulations)}
          {renderSection('🛠️ 작업 기록', 'WORK_ORDERS', recentWorkOrders)}
        </main>
      </div>
    </div>
  );
};

export default MainPage;
